import { useState, useEffect, MouseEvent, useRef } from 'react'
import Tippy from '@tippyjs/react'
import { Instance } from 'tippy.js'
import { useSpring } from '@react-spring/web'
import { i18n as I18n } from 'builder/utils/i18n'
import { useClickOutside } from 'builder/hooks/useClickOutside'
import { Container, Field, Flag, Title, Menu, Scroll, List, Item } from './styles'

const UNSUPPORTED_LOCALES = ['hi', 'he', 'zh-HK']

interface LanguageOption {
  id: string
  name: string
}

interface LanguageSelectProps {
  options: LanguageOption[]
  selected: string
  onSelect: (id: string) => void
}

export const LanguageSelect: React.FC<LanguageSelectProps> = ({ options, selected, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const animationConfig = { duration: 140 }
  const initialAnimationStyles = { opacity: 0, transform: 'scale(1, 0.3)' }
  const [springProps, springApi] = useSpring(() => initialAnimationStyles)

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false)
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isOpen])

  const onMount = () => {
    springApi.start({
      opacity: 1,
      transform: 'scale(1, 1)',
      config: animationConfig,
    })
  }

  const onHide = ({ unmount }: Instance) => {
    springApi.start({
      opacity: 0,
      onRest: () => {
        unmount()
        // additional call here because we need to use only opacity as fade out animation
        // but we also need to restore the state to default before next show
        springApi.start({ ...initialAnimationStyles, config: animationConfig })
      },
      config: { ...animationConfig, clamp: true },
    })
  }

  const handleOpen = () => setIsOpen(true)
  const handleClickOutside = () => isOpen && setIsOpen(false)
  const handleClose = (e: MouseEvent) => {
    e.stopPropagation()
    setIsOpen(false)
  }
  const handleSelect = (id: string) => {
    onSelect(id)
    setIsOpen(false)
  }

  useClickOutside(menuRef, handleClickOutside)

  const withKR = window.location.search.includes('kr')

  const languagesList = options.filter(lang => {
    if (!withKR && lang.id === 'ko-KR') return false
    if (UNSUPPORTED_LOCALES.includes(lang.id)) return false
    return true
  })

  const currentOption = languagesList.find(option => option.id === selected) ||
    options.find(option => option.id === selected && UNSUPPORTED_LOCALES.includes(option.id)) || {
      id: 'unknown',
      name: I18n.t('builder.resume_editor.unknown_language'),
    }

  return (
    <Container>
      <Tippy
        placement="bottom"
        render={attrs => {
          return (
            <Menu onClick={handleClose} style={springProps} {...attrs} ref={menuRef}>
              <Title>{I18n.t('builder.resume_editor.resume_language')}</Title>
              <Scroll>
                <List>
                  {languagesList.map(option => (
                    <Item
                      key={option.id}
                      isSelected={option.id === selected}
                      onClick={() => handleSelect(option.id)}
                    >
                      <Flag locale={option.id} isSelected={option.id === selected} />
                      {option.name}
                    </Item>
                  ))}
                </List>
              </Scroll>
            </Menu>
          )
        }}
        onMount={onMount}
        onHide={onHide}
        visible={isOpen}
        interactive
        animation
        appendTo={() => document.body}
      >
        <Field onClick={handleOpen}>
          <Flag locale={currentOption.id} />
          {currentOption.name}
        </Field>
      </Tippy>
    </Container>
  )
}
